import { effect, inject, Injectable, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { CombinedStore } from 'src/app/core/combined.store';
import { Step } from '../models/model/concretes/components/step.model';
import { NavigationService } from './navigation.service';
import { SharedDataService } from './shared-data.service';
import { SUBSCRIPTION_PATH } from '../constants/route.constants';
import { isPartner } from '../utils/brand-utils';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  currentStep: Step | undefined;
  currentGroupStepIndex: WritableSignal<number>;
  currentStepIndex: WritableSignal<number>;

  subscription = this.sharedDataService.getSubscription();

  store = inject(CombinedStore);

  constructor(
    private sharedDataService: SharedDataService,
    private navigationService: NavigationService,
    private router: Router
  ) {
    this.currentGroupStepIndex = this.sharedDataService.getGroupStep();
    this.currentStepIndex = this.sharedDataService.getStepIndex();

    effect(
      () => {
        const index = this.sharedDataService.getStepIndex();
        if (index != null) {
          this.updateCurrentStep();
        }
      },
      { allowSignalWrites: true }
    );
  }

  //#region Public Methods
  previousBtn(): boolean {
    return this.currentStepIndex() != 0;
  }

  previousStep() {
    if (!this.currentStep) return;

    const groupSteps = this.subscription()?.getGroupSteps();
    if (!groupSteps) return;

    const currentGroup = groupSteps[this.currentGroupStepIndex()];
    if (!currentGroup) return;

    // Désactiver la step courante
    this.currentStep.isActive = false;
    this.subscription()?.setCurrentStep(
      this.currentGroupStepIndex(),
      this.currentStepIndex(),
      this.currentStep
    );

    this.sharedDataService.forceUpdateSubscription();

    let previousStepObj;
    let previousStepIndex = this.currentStepIndex() - 1;

    // Rechercher la step précédente dans le même groupe
    while (previousStepIndex >= 0 && !currentGroup.steps[previousStepIndex]!.isCompleted) {
      previousStepIndex--;
    }

    if (previousStepIndex >= 0) {
      previousStepObj = currentGroup.steps[previousStepIndex];
      this.currentStepIndex.set(previousStepIndex);
    } else if (this.currentGroupStepIndex() > 0) {
      // Reculer au groupe précédent
      const prevGroupIndex = this.currentGroupStepIndex() - 1;
      const prevGroup = groupSteps[prevGroupIndex];
      if (!prevGroup || !prevGroup.steps.length) return;

      // Désactiver toutes les steps du groupe précédent
      prevGroup.steps.forEach(step => (step.isActive = false));

      // Aller à la dernière step complétée du groupe précédent
      let lastCompletedIndex = prevGroup.steps.length - 1;
      while (lastCompletedIndex >= 0 && !prevGroup.steps[lastCompletedIndex]!.isCompleted) {
        lastCompletedIndex--;
      }
      if (lastCompletedIndex < 0) lastCompletedIndex = prevGroup.steps.length - 1; // fallback

      previousStepObj = prevGroup.steps[lastCompletedIndex];
      this.currentGroupStepIndex.set(prevGroupIndex);
      this.currentStepIndex.set(lastCompletedIndex);
    }

    if (previousStepObj) {
      // ⚡ 2. Mettre à jour la step courante
      this.currentStep = previousStepObj;

      // ⚡ 3. Activer uniquement cette step
      this.currentStep.isActive = true;
      this.subscription()?.setCurrentStep(
        this.currentGroupStepIndex(),
        this.currentStepIndex(),
        this.currentStep
      );

      // ⚡ 4. Naviguer vers la step
      const targetUrl = [SUBSCRIPTION_PATH, ...this.currentStep.url.split('/')];
      if (this.router.url !== targetUrl.join('/')) {
        this.router.navigate(targetUrl);
      }
    }

    this.updateCurrentStep();
  }

  previousGroupStep() {
    this.navigationService.updateSubmitted(false);

    const groupSteps = this.subscription()?.getGroupSteps();
    if (!groupSteps || this.currentGroupStepIndex() === 0) return; // Pas de groupe précédent

    const oldGroupIndex = this.currentGroupStepIndex();
    const oldStepIndex = this.currentStepIndex();

    // Désactiver la step courante avant de reculer
    if (this.currentStep) {
      this.currentStep.isActive = false;
      this.currentStep.isCompleted = true; // ou false si tu veux pas marquer comme complétée
      this.subscription()?.setCurrentStep(oldGroupIndex, oldStepIndex, this.currentStep);
    }

    this.sharedDataService.forceUpdateSubscription();

    const prevGroupIndex = oldGroupIndex - 1;
    const prevGroup = groupSteps[prevGroupIndex];
    if (!prevGroup || !prevGroup.steps.length) return;

    // Désactiver toutes les steps du groupe précédent
    prevGroup.steps.forEach(step => (step.isActive = false));

    // Aller à la dernière step complétée du groupe précédent
    let lastCompletedIndex = prevGroup.steps.length - 1;
    while (lastCompletedIndex >= 0 && !prevGroup.steps[lastCompletedIndex]!.isCompleted) {
      lastCompletedIndex--;
    }
    if (lastCompletedIndex < 0) lastCompletedIndex = prevGroup.steps.length - 1; // fallback sur la dernière step

    // Mettre à jour indices et step courante
    this.currentGroupStepIndex.set(prevGroupIndex);
    this.currentStepIndex.set(lastCompletedIndex);
    this.currentStep = prevGroup.steps[lastCompletedIndex];

    // Activer la step
    this.currentStep!.isActive = true;
    this.subscription()?.setCurrentStep(
      this.currentGroupStepIndex(),
      this.currentStepIndex(),
      this.currentStep!
    );

    // Naviguer vers la step
    const targetUrl = [SUBSCRIPTION_PATH, ...this.currentStep!.url.split('/')];
    if (this.router.url !== targetUrl.join('/')) {
      this.router.navigate(targetUrl);
    }

    this.updateCurrentStep();
  }

  nextBtn(): boolean {
    return (
      this.currentGroupStepIndex() != (this.subscription()?.groupSteps?.length ?? 0) - 1 ||
      this.currentStepIndex() !=
        (this.subscription()?.getStepsCountInGroup(this.currentGroupStepIndex()) ?? 0) - 1
    );
  }

  nextStep(url?: string) {
    this.navigationService.updateSubmitted(false);

    // Désactiver step courante
    if (this.currentStep) {
      this.currentStep.isActive = false;
      this.currentStep.isCompleted = true;
      this.subscription()?.setCurrentStep(
        this.currentGroupStepIndex(),
        this.currentStepIndex(),
        this.currentStep
      );
    }

    this.sharedDataService.forceUpdateSubscription();

    const stepCount =
      (this.subscription()?.getStepsCountInGroup(this.currentGroupStepIndex()) ?? 0) - 1;

    let nextStepObj: Step | undefined;

    if (url != null) {
      // Naviguer directement vers une URL
      const stepResult = this.subscription()?.activateStepByUrl(url, this.currentGroupStepIndex());
      nextStepObj = stepResult ?? undefined;

      if (nextStepObj) {
        this.currentStepIndex.set(
          this.subscription()
            ?.getGroupStep(this.currentGroupStepIndex())
            ?.steps.indexOf(nextStepObj) ?? 0
        );
      }
    } else if (this.currentStepIndex() < stepCount) {
      // Step suivante classique
      this.currentStepIndex.set(this.currentStepIndex() + 1);
      nextStepObj = this.subscription()?.getGroupStep(this.currentGroupStepIndex())?.steps[
        this.currentStepIndex()
      ];
    } else {
      // Dernière step du groupe → passer au groupe suivant
      this.nextGroupStep();
      return;
    }

    if (nextStepObj) {
      this.currentStep = nextStepObj;
      this.activateStep(this.currentStep);

      // Mettre à jour la step courante sans forcer la navigation (nextStep gère déjà ça)
      this.updateCurrentStep(false);
    }
  }

  nextGroupStep(url?: string) {
    this.navigationService.updateSubmitted(false);

    const oldGroupIndex = this.currentGroupStepIndex();
    const oldStepIndex = this.currentStepIndex();

    // Désactiver la step courante avant de passer au nouveau groupe
    if (this.currentStep) {
      this.currentStep.isActive = false;
      this.currentStep.isCompleted = true;
      this.subscription()?.setCurrentStep(oldGroupIndex, oldStepIndex, this.currentStep);
    }

    this.sharedDataService.forceUpdateSubscription();

    // Avancer au groupe suivant
    this.sharedDataService.nextGroupStep();
    this.resetStepOfGroupStep();

    const nextGroup = this.subscription()?.getGroupStep(this.currentGroupStepIndex());
    if (!nextGroup || !nextGroup.steps.length) return;

    // Désactiver toutes les steps du nouveau groupe
    nextGroup.steps.forEach(step => (step.isActive = false));

    let nextStepObj: Step | undefined;

    if (url != null) {
      const stepResult = this.subscription()?.activateStepByUrl(url, this.currentGroupStepIndex());
      if (stepResult) {
        nextStepObj = stepResult;
        this.currentStepIndex.set(nextGroup.steps.indexOf(stepResult));
      }
    }

    if (!nextStepObj) {
      // Sinon première step du groupe
      this.currentStepIndex.set(0);
      nextStepObj = nextGroup.steps[0];
      this.subscription()?.activateStepByUrl(nextStepObj!.url, this.currentGroupStepIndex());
    }

    if (nextStepObj) {
      this.currentStep = nextStepObj;
      this.activateStep(this.currentStep);

      // Mise à jour de la step courante sans forcer la navigation (gérée par nextGroupStep)
      this.updateCurrentStep(false);
    }
  }
  //#endregion

  //#region Private Methods
  public updateCurrentStep(skipNavigation = false) {
    // Récupérer la step courante depuis le modèle
    this.currentStep = this.subscription()?.getCurrentStep(
      this.currentGroupStepIndex(),
      this.currentStepIndex()
    );

    if (!this.currentStep) return;

    // Marquer la step comme active
    this.currentStep.isActive = true;
    this.subscription()?.setCurrentStep(
      this.currentGroupStepIndex(),
      this.currentStepIndex(),
      this.currentStep
    );

    const index = this.subscription()?.findStepIndexesByUrl(this.router.url.split('/')[2]!);

    this.sharedDataService.setStep((index?.stepIndex ?? 0) + 1);

    // Ne pas afficher l'étape "Information" du Client si le parcours est issu d'une Fiche Client
    // Afficher directement l'étape suivante
    if (
      this.currentStep?.url == 'subscriber' &&
      this.store.context().souscripteur?.email &&
      !isPartner(environment.brand)
    ) {
      this.nextStep();
      return;
    }

    // Mettre à jour header et navigation state
    if (this.currentStep.layoutInfo) {
      this.setHeader(this.currentStep);
      this.navigationService.setNavigationState({
        groupIndex: this.currentGroupStepIndex(),
        stepNumber: this.currentStepIndex(),
        isStepValid: false,
        subscriberCGVm: null,
        url: this.currentStep.url
      });
    }

    // Navigation Angular seulement si nécessaire
    if (!skipNavigation) {
      const targetUrl = [SUBSCRIPTION_PATH, ...this.currentStep.url.split('/')].join('/');
      const normalizedCurrent = this.router.url.replace(/\/+$/, '');
      const normalizedTarget = targetUrl.replace(/\/+$/, '');

      if (normalizedCurrent !== normalizedTarget) {
        this.router.navigate([targetUrl]);
      }
    }
  }

  private setHeader(step: Step) {
    this.sharedDataService.setLayoutInfo(step.layoutInfo);
    this.sharedDataService.setStepShow(step.layoutInfo.stepShow);
    this.setMaxStep();
    this.setMaxStepShow();
  }

  public setMaxStep() {
    const groupStepIndex = this.currentGroupStepIndex();
    if (groupStepIndex !== undefined) {
      this.sharedDataService.setMaxStep(
        this.subscription()?.getStepsCountInGroup(groupStepIndex) ?? 0
      );
    }
  }

  private setMaxStepShow() {
    const maxStep = this.subscription()?.getLastStepInGroupStep(this.currentGroupStepIndex());
    if (maxStep) this.sharedDataService.setMaxStepShow(maxStep);
  }

  private resetStepOfGroupStep() {
    this.sharedDataService.setStep(1);
    this.sharedDataService.setStepIndex(0);
  }

  private activateStep(step: Step) {
    step.isActive = true;
    step.isCompleted = false;
    this.subscription()?.setCurrentStep(
      this.currentGroupStepIndex(),
      this.currentStepIndex(),
      step
    );
  }
  //#endregion
}
